import { Link } from "@tanstack/react-router";
import { Globe, Store, ShoppingBag, Scale, Mail, Phone, MapPin, Hash, CreditCard } from "lucide-react";
import logoAsset from "@/assets/kupish-logo.png.asset.json";
import { NewsletterSection } from "./NewsletterSection";

type FooterLink = { to: string; label: string };

function Column({
  icon: Icon,
  title,
  links,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  links: FooterLink[];
}) {
  return (
    <div className="text-left">
      <div className="flex items-center gap-2.5 mb-5">
        <span className="icon-badge-sm">
          <Icon className="h-4 w-4" />
        </span>
        <h3 className="font-heading text-xl leading-none tracking-tight">{title}</h3>
      </div>
      <ul className="space-y-2.5 text-sm text-muted-foreground">
        {links.map((l) => (
          <li key={l.to}>
            <Link
              to={l.to}
              className="inline-flex items-center gap-1.5 hover:text-accent transition-colors"
            >
              <span className="text-accent/60">·</span>
              {l.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function SiteFooter() {
  return (
    <footer className="mt-32 border-t border-border bg-gradient-to-b from-muted/20 to-muted/50">
      <NewsletterSection />
      <div className="mx-auto max-w-7xl px-4 pt-16 pb-10 md:px-6 md:pt-20">
        {/* Brand intro */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <img
            src={logoAsset.url}
            alt="kupish.si"
            width={240}
            height={80}
            decoding="async"
            className="h-14 w-auto mx-auto"
          />
          <p className="mt-5 font-heading text-xl leading-snug sm:text-2xl md:text-3xl">
            Spletno nakupovalno središče <span className=" text-accent">novih izdelkov</span> različnih ponudnikov in trgovin.
          </p>
          <div className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-muted-foreground">
            <span className="h-px w-8 bg-border" />
            Izdelki za vsak dan
            <span className="h-px w-8 bg-border" />
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-10 lg:gap-6">
          <Column
            icon={Globe}
            title="Koristne povezave"
            links={[
              { to: "/izdelki", label: "Vsi izdelki" },
              { to: "/kategorije", label: "Kategorije" },
              { to: "/navdih", label: "Navdih & zgodbe" },
              { to: "/o-nas", label: "O nas" },
              { to: "/pogosta-vprasanja", label: "Pogosta vprašanja" },
            ]}
          />
          <Column
            icon={Store}
            title="Prodajalci"
            links={[
              { to: "/najem-spletne-trgovine", label: "Najem spletne trgovine" },
              { to: "/placilo-najemnine", label: "Plačilo letne najemnine" },
              { to: "/registracija", label: "Registracija prodajalca" },
              { to: "/prijava", label: "Prijava prodajalca" },
              { to: "/uporabniska-navodila-ustvarjalci", label: "Navodila za prodajalce" },
            ]}
          />
          <Column
            icon={ShoppingBag}
            title="Kupci"
            links={[
              { to: "/racun", label: "Moj račun" },
              { to: "/kosarica", label: "Moja košarica" },
              { to: "/narocila", label: "Moja naročila" },
              { to: "/uporabniska-navodila", label: "Uporabniška navodila" },
              { to: "/zaupanje-in-varnost", label: "Zaupanje in varnost" },
            ]}
          />
          <Column
            icon={Scale}
            title="Pravno"
            links={[
              { to: "/povzetek-pogojev", label: "Povzetek pogojev poslovanja" },
              { to: "/pogoji", label: "Splošni pogoji poslovanja" },
              { to: "/zasebnost", label: "Pravila zasebnosti" },
              { to: "/reklamacije", label: "Reševanje reklamacij" },
              { to: "/piskotki", label: "Piškotki" },
            ]}
          />


          {/* Kontakt column */}
          <div className="text-left">
            <div className="flex items-center gap-2.5 mb-5">
              <span className="icon-badge-sm">
                <Mail className="h-4 w-4" />
              </span>
              <h3 className="font-heading text-xl leading-none tracking-tight">Kontakt</h3>
            </div>
            <address className="not-italic space-y-3 text-sm text-muted-foreground">
              <div>
                <p className="font-semibold text-foreground leading-tight">
                  ARTEPIN,<br />
                  spletne rešitve, d.o.o.
                </p>
                <p className="flex items-start gap-2 mt-1.5">
                  <MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0 text-accent/70" />
                  <span>Šišenska cesta 17,<br />1000 Ljubljana - SI</span>
                </p>
              </div>
              <div className="space-y-1">
                <p className="flex items-center gap-2">
                  <Globe className="h-3.5 w-3.5 shrink-0 text-accent/70" />
                  <a
                    href="https://www.kupish.si"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-accent"
                  >
                    www.kupish.si
                  </a>
                </p>
              </div>
              <div className="space-y-1">
                <p className="flex items-center gap-2">
                  <Hash className="h-3.5 w-3.5 shrink-0 text-accent/70" />
                  <span>Davčna: 49460315</span>
                </p>
                <p className="flex items-center gap-2">
                  <Hash className="h-3.5 w-3.5 shrink-0 text-accent/70" />
                  <span>Matična: 8626324000</span>
                </p>
              </div>
              <div>
                <p className="flex items-center gap-2">
                  <CreditCard className="h-3.5 w-3.5 shrink-0 text-accent/70" />
                  <span>IBAN: SI56 6100 0002 3812 295</span>
                </p>
              </div>
              <div className="space-y-1">
                <p className="flex items-center gap-2">
                  <Mail className="h-3.5 w-3.5 shrink-0 text-accent/70" />
                  <a
                    href="mailto:info@kupish.si"
                    className="hover:text-accent"
                  >
                    info@kupish.si
                  </a>
                </p>
                <p className="flex items-center gap-2">
                  <Phone className="h-3.5 w-3.5 shrink-0 text-accent/70" />
                  <a href="tel:+386070783641" className="hover:text-accent">Mobi: 070 783 641</a>
                </p>
              </div>
            </address>
          </div>
        </div>
      </div>

      <div className="border-t border-border bg-background/40">
        <div className="mx-auto max-w-7xl px-4 py-6 text-xs text-muted-foreground flex flex-col sm:flex-row items-center justify-center gap-3 text-center md:px-6">
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
            <Link to="/admin" className="hover:text-accent">Admin</Link>
            <span>© 2026 ARTEPIN d.o.o.</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
